/**
 * TestimonialsSection
 *
 * A 3x2 grid section alternating between testimonial cards and images.
 * Row 1: testimonial (tear-drop left shape), image (with quotation overlay), testimonial (tear-drop right shape)
 * Row 2: image (with quotation overlay), testimonial (tear-drop bottom shape), image (with quotation overlay)
 * On mobile, testimonials stack vertically.
 *
 * Used on consulting service pages to display social proof from rescue partners.
 *
 * Props:
 * - bgColor (string, optional): Tailwind bg class for the section. Defaults to "".
 * - title (string | ReactNode, required): Section heading.
 * - description (string, required): Subtitle text below the heading.
 * - testimonials (array, required): Array of testimonial objects with shape:
 *     - text (string): The testimonial quote.
 *     - author (string): The author's name.
 *     - organization (string): The author's organization.
 *     - initials (string): Two-letter initials for the avatar.
 * - cardBg (string, optional): Tailwind bg class for testimonial cards. Defaults to "bg-orange".
 * - images (array, required): Array of exactly 3 image objects:
 *     - src (string): Image URL.
 *     - alt (string): Alt text.
 *     - bg (string, optional): Tailwind bg class applied behind the image (e.g. "bg-orange").
 * - showHorizontalLine (boolean, optional): Whether to show the horizontal line above the title. Defaults to true.
 */

const TestimonialsSection = ({
  bgColor = "",
  title,
  description,
  testimonials,
  cardBg = "bg-orange",
  images,
  showHorizontalLine = true,
}) => {
  const TestimonialCard = ({ testimonial, shape }) => (
    <div className={`${cardBg} ${shape} h-full p-8 lg:p-10 flex flex-col justify-center items-center text-left text-black`}>
      <p className="!text-xs xl:!text-base !font-medium leading-relaxed mb-6">
        {testimonial.text}
      </p>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-xs xl:!text-base font-bold">
          {testimonial.initials}
        </div>
        <div>
          <p className="font-bold !text-sm xl:!text-base text-blue-dark1">{testimonial.author}</p>
          <p className="!text-xs xl:!text-base text-blue-dark1">{testimonial.organization}</p>
        </div>
      </div>
    </div>
  );

  const ImageCard = ({ image }) => (
    <div className={`${image.bg ?? ""} ${image.className ?? ""} h-[300px] lg:h-[350px] rounded-2xl overflow-hidden relative`}>
      <img
        src={image.src}
        alt={image.alt}
        className="w-full h-full object-cover"
        loading="lazy"
      />
      <img
        src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/quotation.svg"
        alt=""
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 lg:w-24"
      />
    </div>
  );

  return (
    <section className={`${bgColor} py-16 mb-16 px-4 md:px-8 lg:px-16 xl:px-24`}>
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="mb-12 text-center md:text-left">
          {showHorizontalLine && (
            <img
              src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/horizontal-line.svg"
              alt=""
              className="w-28 md:w-40 mx-auto md:mx-0 mb-4"
            />
          )}
          <h2 className="text-2xl md:text-3xl lg:text-5xl font-bold text-blue-dark1 mb-4">
            {title}
          </h2>
          <p className="!text-sm md:!text-base font-medium mt-4 max-w-md mx-auto md:mx-0">
            {description}
          </p>
        </div>

        {/* Desktop/Tablet grid */}
        <div className="hidden md:grid grid-cols-3 gap-4 lg:gap-6">
          {/* Row 1: testimonial, image, testimonial */}
          <TestimonialCard testimonial={testimonials[0]} shape="rounded-[50%_50%_1rem_50%]" />
          <ImageCard image={images[0]} />
          <TestimonialCard testimonial={testimonials[1]} shape="rounded-[50%_1rem_50%_50%]" />

          {/* Row 2: image, testimonial, image */}
          <ImageCard image={images[1]} />
          <TestimonialCard testimonial={testimonials[2]} shape="rounded-[50%_50%_50%_1rem]" />
          <ImageCard image={images[2]} />
        </div>

        {/* Mobile stacked layout */}
        <div className="flex flex-col gap-4 md:hidden">
          {testimonials.map((testimonial, i) => (
            <div key={i} className={`${cardBg} rounded-2xl p-6`}>
              <p className="!text-sm leading-relaxed mb-4 text-left">{testimonial.text}</p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center !text-xs font-bold text-blue-dark1">
                  {testimonial.initials}
                </div>
                <div>
                  <p className="font-bold !text-sm text-blue-dark1 text-left">{testimonial.author}</p>
                  <p className="!text-xs opacity-80 text-blue-dark1 text-left">{testimonial.organization}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default TestimonialsSection;
